import React from "react";
import { useTranslation } from "react-i18next";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Globe, Check } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "@/i18n";

export default function LanguageSwitcher({ compact = false, side = "bottom" }) {
    const { i18n, t } = useTranslation();
    const current =
        SUPPORTED_LANGUAGES.find((l) => i18n.language?.startsWith(l.code)) ||
        SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) ||
        SUPPORTED_LANGUAGES[0];

    const change = (code) => {
        i18n.changeLanguage(code);
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size={compact ? "icon" : "sm"}
                    className={
                        compact
                            ? "h-10 w-10 rounded-xl text-[#1B2621] hover:bg-[rgba(26,154,107,0.08)]"
                            : "h-10 rounded-xl px-3 gap-2 text-[#1B2621] hover:bg-[rgba(26,154,107,0.08)]"
                    }
                    aria-label={t("languageSwitcher.label")}
                    data-testid="language-switcher-trigger"
                >
                    <Globe className="h-4 w-4" />
                    {!compact && (
                        <span className="text-sm font-medium">
                            {current.label}
                        </span>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                side={side}
                className="w-56 bg-[#FBF7EE] border-[#E9D9C1]"
            >
                <DropdownMenuLabel className="text-[#062A1E] font-semibold">
                    {t("languageSwitcher.label")}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[#E9D9C1]" />
                {SUPPORTED_LANGUAGES.map((lang) => (
                    <DropdownMenuItem
                        key={lang.code}
                        onClick={() => change(lang.code)}
                        className="cursor-pointer focus:bg-[#F4EBDD] text-[#062A1E]"
                        data-testid={`language-option-${lang.code}`}
                    >
                        <span className="mr-2" aria-hidden>
                            {lang.flag}
                        </span>
                        <span className="flex-1">{lang.label}</span>
                        {current.code === lang.code && (
                            <Check className="h-4 w-4 text-[#0B4A34]" />
                        )}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
